<template>
  <div
    :class="['page-container', {
      'water-taxi': routeInfo.watertaxi,
    }]"
  >
    <div
      ref="mapContainerRef"
      :class="['map-container full-width', {
        'lt-md': !pointToChooseOnMap,
      }]"
    >
      <GRouteMap
        v-if="$q.screen.lt.md || pointToChooseOnMap"
        ref="mapRef"
        class="map fix-gm full-height"
        :from="pointToChooseOnMap === 'from' ? {} : transfer.from"
        :to="pointToChooseOnMap === 'to' ? {} : transfer.to"
        :distance="pointToChooseOnMap ? null : routeInfo.distance_preferred"
        :gmap-zoom="1"
        :time="pointToChooseOnMap ? null : routeInfo.duration"
        :no-zoom-marker="!!pointToChooseOnMap"
        :type="type"
        :route-info="routeInfo"
        is-transfer-creation
        @map-initialized="initTransferRoute"
        @click="!pointToChooseOnMap && fillPointOnClick($event, 'from')"
        @click-on-marker="!pointToChooseOnMap && chooseOnMap($event)"
      />
      <q-icon
        v-if="pointToChooseOnMap"
        class="pin-on-map absolute-center"
        name="place"
        color="primary"
        size="lg"
      />
    </div>
    <div class="gt-sm container q-my-24">
      <h1 class="q-my-none text-bold">{{ $t('header.h1.main') }}</h1>
      <div class="q-gutter-x-8 q-mt-8">
        <div
          v-for="item in $tm('transfer.options')"
          :key="item"
          class="flex inline items-center text-weight-medium"
        >
          <q-icon
            class="q-mr-4"
            color="primary"
            name="done"
          />
          {{ item }}
        </div>
      </div>
    </div>
    <q-form
      ref="formRef"
      class="container flex no-wrap justify-center bg-white form-container"
      no-error-focus
      @submit="createTransfer"
      @validation-error="onValidationError"
    >
      <div class="column no-wrap full-width gap-12">
        <template v-if="!pointToChooseOnMap">
          <TransferTypeToggle
            v-model="transferType"
            class="gt-sm full-width"
            with-delivery
          />
          <TransferTypeTabs
            v-model="transferType"
            class="lt-md"
            @tab-visible="onTabVisible"
          />
        </template>
        <TransferAddressInputs
          ref="addressInputsRef"
          v-model:from="transfer.from"
          v-model:to="transfer.to"
          v-model:mapPinFor="mapPinFor"
          :show-directions-symbol="!isCarRental && !isExperience"
          :is-round-trip="isRoundTrip"
          :lazy-rules="lazyRules"
          :region-forbidden="regionForbidden"
          :with-to-point="withToPoint"
          :point-to-choose-on-map="pointToChooseOnMap"
          @choose-on-map="chooseOnMap"
          @choose-on-map.once="sendTransferAnalytics('passenger_click_choose_on_map')"
          @clear="onInputClear"
          @update:from.once="sendTransferAnalytics('passenger_enter_from')"
          @update:to.once="sendTransferAnalytics('passenger_enter_to')"
          @swap.once="sendTransferAnalytics('passenger_click_change_point')"
        />
        <q-toggle
          v-if="isHourly && !pointToChooseOnMap"
          v-model="hourlyDifferentTo"
          class="toggle-margin"
          :label="$t('label.end_in_another_location')"
          @update:modelValue="transfer.to = {}"
        />
        <q-slide-transition>
          <div
            v-if="!isCarRental && !isExperience && routeInfo.watertaxi"
            class="fs-14 lt-md text-grey-6 text-right q-px-8"
          >
            {{ $t('label.include_water_taxi') }}
          </div>
        </q-slide-transition>
        <div
          v-if="pointToChooseOnMap"
          :class="['text-center', { 'q-pb-16': isWeb && $q.platform.is.mobile }]"
        >
          <q-btn
            class="q-px-24"
            :label="$t('button.done')"
            color="positive"
            :loading="geocoderLoading"
            @click="chooseOnMap(null)"
            @click.once="sendTransferAnalytics('passenger_set_point_on_map')"
          />
        </div>
        <template v-else>
          <TransferFormPartnerButton
            v-if="isCarRental || isExperience"
            :is-car-rental="isCarRental"
            :transfer="transfer"
            @click="onPartnerButtonClick"
          />
          <template v-else>
            <q-slide-transition v-if="showFormPrices">
              <TransferTransportTypes
                v-if="!isDelivery"
                v-model="transfer.transport_type_ids"
                :route="routeInfo"
              />
            </q-slide-transition>
            <q-slide-transition>
              <TransferRideDate
                v-model:isRoundTrip="isRoundTrip"
                :is-hourly="isHourly"
                :is-route="isRoute"
                :transfer="transfer"
                :lazy-rules="lazyRules"
                :min-date="routeInfo.min_date"
                @send-transfer-analytics="sendTransferAnalytics"
                @update-trip="(direction, key, value) => transfer[direction][key] = value"
              >
                <template
                  v-if="!isDelivery"
                  #after-to-date
                >
                  <WaitingTime
                    :point="transfer.from"
                    :date="transfer.trip_to.date"
                  />
                  <q-slide-transition>
                    <FlightNumberInput
                      v-if="isFromAirport"
                      v-model="transfer.trip_to.flight_number"
                      :label="$t('transfer.flight_number', {})"
                      outlined
                      @focus.once="sendTransferAnalytics('passenger_open_flight')"
                      @update:modelValue.once="sendTransferAnalytics('passenger_enter_flight')"
                    />
                  </q-slide-transition>
                  <q-slide-transition>
                    <NameSignInput
                      v-if="showFromNameSignInput"
                      v-model="transfer.name_sign"
                      outlined
                      @focus.once="sendTransferAnalytics('passenger_open_name_sign')"
                      @update:modelValue.once="sendTransferAnalytics('passenger_enter_name_sign')"
                    />
                  </q-slide-transition>
                </template>
                <template #after-return-date>
                  <WaitingTime
                    :point="transfer.to"
                    :date="transfer.trip_return.date"
                  />
                  <q-slide-transition>
                    <FlightNumberInput
                      v-if="isToAirport"
                      v-model="transfer.trip_return.flight_number"
                      class="q-mt-12"
                      :label="$t('transfer.flight_number', {})"
                      outlined
                      @focus.once="sendTransferAnalytics('passenger_open_flight')"
                      @update:modelValue.once="sendTransferAnalytics('passenger_enter_flight')"
                    />
                  </q-slide-transition>
                  <q-slide-transition>
                    <NameSignInput
                      v-if="showToNameSignInput"
                      v-model="transfer.name_sign"
                      class="q-mt-12"
                      outlined
                      @focus.once="sendTransferAnalytics('passenger_open_name_sign')"
                      @update:modelValue.once="sendTransferAnalytics('passenger_enter_name_sign')"
                    />
                  </q-slide-transition>
                </template>
              </TransferRideDate>
            </q-slide-transition>
            <TransferAdults
              v-if="!isDelivery"
              v-model="adults"
              :lazy-rules="lazyRules"
              @update:modelValue="adultsChangedManually = true"
              @update:modelValue.once="sendTransferAnalytics('passenger_edit_adults')"
            />
            <q-slide-transition>
              <div v-if="!isDelivery">
                <TransferChildSeats
                  :transfer="transfer"
                  @update:modelValue="(key, value) => transfer[key] = value"
                  @update:modelValue.once="sendTransferAnalytics('passenger_edit_child_seats')"
                  @show-modal.once="sendTransferAnalytics('passenger_open_child_seats')"
                />
                <q-slide-transition>
                  <PartnerPassenger
                    v-if="hasPartnerPassenger"
                    v-model="passenger"
                    class="q-mt-12"
                    hide-bottom-space
                    outlined
                  />
                </q-slide-transition>
              </div>
            </q-slide-transition>
            <HintsInput
              ref="commentInputRef"
              v-model.trim="transfer.comment"
              :hints="routeInfo.hints_to_comments"
              autocomplete="off"
              :label="$t('label.comment')"
              :placeholder="$t(`placeholder.comment${isDelivery ? '_delivery' : ''}`)"
              :rules="isDelivery ? [v => requiredField(v)] : []"
              outlined
              stack-label
              @update:modelValue.once="sendTransferAnalytics('passenger_enter_comment')"
            />
            <q-slide-transition>
              <q-select
                v-if="reasons.length"
                v-model="transfer.transfer_reason_id"
                option-label="title"
                option-value="id"
                emit-value
                map-options
                :options="reasons"
                :label="isFamilyUiType
                  ? $t('transfer.trip_type')
                  : $t('transfer.reason_for_transfer')
                "
                clearable
                outlined
                clear-icon="close"
              />
            </q-slide-transition>
            <AirlineCardFields
              v-if="isWeb && domain.airlineCard"
              :transfer="transfer"
              @update-value="(key, val) => transfer[key] = val"
            />
            <q-slide-transition v-else>
              <TransferPromoCode
                v-if="!promoCodeRestricted"
                ref="promoCodeInputRef"
                v-model="transfer.promo_code"
                :loading="isLoading"
                @loading="isLoading = $event"
                @show-auth-modal="showAuthModal"
                @show-promo-code-input="onShowPromoCodeInput"
                @update:modelValue.once="sendTransferAnalytics('passenger_enter_promocode')"
              />
            </q-slide-transition>
            <q-field
              v-if="mustAcceptTerms"
              class="terms-field"
              :model-value="termsAccepted"
              :rules="[v => requiredField(v, $t('error.terms_accepted'))]"
              :lazy-rules="lazyRules"
              dense
              hide-bottom-space
              borderless
              no-error-icon
            >
              <template #control>
                <span
                  @click="openCapacitorBrowser"
                  v-html="$t(`label.accept_terms${mustAcceptPartnership ? '_and_partnership' : ''}`, {
                    service: `${configs.base_url}/${$i18n.locale}/terms_of_use?layout=${isWeb}`,
                    partnership: `${configs.base_url}/${$i18n.locale}/partnership_agreement?layout=${isWeb}`,
                  })"
                />
              </template>
              <template #prepend>
                <q-toggle
                  v-model="termsAccepted"
                  class="toggle-margin text-black"
                />
              </template>
            </q-field>
            <div
              v-intersection="{
                handler: onBottomIntersection,
                cfg: {
                  rootMargin: '0px 0px -104px 0px',
                },
              }"
              class="observer"
            />
            <div class="sticky-btn text-center no-pointer-events ios-bottom-padding">
              <q-btn
                ref="floatingBtnGetOffersRef"
                :class="['all-pointer-events get-offers', { 'full-width': scrolledToBottom }]"
                color="primary"
                type="submit"
                :label="$t('button.get_offers')"
                :loading="isLoading"
                size="18px"
                @click="lazyRules = false"
              />
            </div>
            <BestPrice
              v-if="isWeb"
              class="lt-md q-my-16"
            />
          </template>
        </template>
      </div>
      <div
        v-if="!pointToChooseOnMap"
        class="secondary gt-sm full-width"
      >
        <div class="q-mb-24">
          <GRouteMap
            v-if="$q.screen.gt.sm"
            class="map"
            :cursor-map="mapPinFor || pointDirection"
            :from="transfer.from"
            :to="transfer.to"
            :distance="routeInfo.distance_preferred"
            :gmap-zoom="1.5"
            :time="routeInfo.duration"
            :type="type"
            :route-info="routeInfo"
            draggable
            is-transfer-creation
            @click="fillPointOnClick"
            @set-direction="setDirection"
            @map-initialized="initTransferRoute"
          />
          <q-skeleton
            v-else
            height="440px"
          />
        </div>
        <q-slide-transition>
          <div
            v-if="routeInfo.watertaxi"
            class="fs-14 text-grey-6"
          >
            {{ $t('label.include_water_taxi') }}
          </div>
        </q-slide-transition>
        <BestPrice v-if="isWeb" />
      </div>
    </q-form>
    <q-scroll-observer
      v-if="isCapacitor && $q.platform.is.ios"
      @scroll="onScroll"
    />
    <GtLoading
      :showing="showLoader || prefillTransferLoading"
      size="24px"
    />
    <RestrictedRegionModal v-if="regionForbidden" />
  </div>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { debounce, useQuasar } from 'quasar';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import {
  computed,
  inject,
  isRef,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue';

import BestPrice from './BestPrice';
import GRouteMap from './GRouteMap';
import PartnerPassenger from './PartnerPassenger';
import TransferTypeToggle from './TransferTypeToggle';
import TransferTypeTabs from './TransferTypeTabs';
import AirlineCardFields from './AirlineCardFields';
import TransferFormPartnerButton from './TransferFormPartnerButton';
import HintsInput from './HintsInput';
import RestrictedRegionModal from './transfer/RestrictedRegionModal';
import TransferAddressInputs from './transfer/TransferAddressInputs';
import TransferAdults from './transfer/TransferAdults';
import TransferChildSeats from './transfer/TransferChildSeats';
import TransferTransportTypes from './transfer/TransferTransportTypes';
import FlightNumberInput from './transfer/FlightNumberInput';
import TransferPromoCode from './transfer/TransferPromoCode';
import TransferRideDate from './transfer/TransferRideDate';
import NameSignInput from './transfer/NameSignInput';
import GtLoading from './GtLoading';
import WaitingTime from './WaitingTime';

import useTransferType from '~composables/useTransferType';
import useSettings from '~composables/useSettings';
import useTransferForm from '~composables/useTransferForm';
import useRouteInfo from '~composables/useRouteInfo';
import useGeocode from '~composables/useGeocode';
import useRequiredFieldRules from '~composables/useRequiredFieldRules';
import useCapacitorBrowser from '~composables/useCapacitorBrowser';

import childSeats from '~common/utils/childSeats';

import { useAccountStore } from '~stores/account';
import { useConfigsStore } from '~stores/configs';

const adultSeats = {
  business: 2,
  comfort: 2,
  economy: 2,
  limousine: 2,
  premium: 2,
  suv: 4,
  van: 4,
  bus: 8,
  minibus: 8,
};

const fieldsToSaveList = [
  'adults',
  'isRoundTrip',
  'hourlyDifferentTo',
  'passenger',
  'transfer',
  'transferType',
];

const pick = (obj, keys) => keys.reduce((acc, field) => {
  const value = isRef(obj[field]) ? obj[field].value : obj[field];
  if (value !== undefined) {
    acc[field] = value;
  }
  return acc;
}, {});

const props = defineProps({
  redirectRouteName: {
    type: String,
    default: null,
  },
  redirectToCabinet: {
    type: Boolean,
    default: false,
  },
});

const route = useRoute();
const router = useRouter();
const { locale } = useI18n();
const $q = useQuasar();

const accountStore = useAccountStore();
const configsStore = useConfigsStore();

const axios = inject('axios', undefined);
const domain = inject('domain');
const moment = inject('moment');
const bus = inject('bus');
const isCapacitor = inject('isCapacitor');
const isWeb = inject('isWeb');

let isClickPartnersTabSent;
let geocodeInitialized;
let iosNotchEl;

const mapRef = ref(null);
const mapContainerRef = ref(null);
const formRef = ref(null);
const addressInputsRef = ref(null);
const commentInputRef = ref(null);
const floatingBtnGetOffersRef = ref(null);
const promoCodeInputRef = ref(null);

const showLoader = ref(!configsStore.configs.proxy_autocomplete);
const prefillTransferLoading = ref(false);
const zoomMap = ref(true);
const isLoading = ref(false);
const adults = ref(2);
const adultsChangedManually = ref(false);
const isRoundTrip = ref(false);
const hourlyDifferentTo = ref(false);
const lazyRules = ref('ondemand');
const passenger = ref(undefined);
const routeInfo = ref({
  distance_preferred: null,
  duration: null,
  hints_to_comments: [],
  min_date: null,
  prices: null,
  watertaxi: false,
});
const pointToChooseOnMap = ref(null);
const reasons = ref([]);
const scrolledToBottom = ref(false);
const mapPinFor = ref(null);
const termsAccepted = ref(false);
const transfer = reactive({
  from: {
    name: '',
    place_id: '',
    point: '',
    chosen_on_map: false,
    types: [],
  },
  to: {
    name: '',
    place_id: '',
    point: '',
    chosen_on_map: false,
    types: [],
  },
  trip_to: {
    date: '',
    date_end: '',
    flight_number: '',
  },
  trip_return: {
    date: '',
    flight_number: '',
  },
  transport_type_ids: [],
  child_seats_infant: 0,
  child_seats_convertible: 0,
  child_seats_booster: 0,
  name_sign: '',
  comment: '',
  transfer_reason_id: null,
  promo_code: '',
  airline_card: '',
  airline_card_name: '',
  airline_card_surname: '',
});
const isSubmitting = ref(false);

const { getTransferType } = useTransferType();
const transferType = ref(getTransferType(null, true));

const { values } = useSettings();
const { geocode, initGeocode, geocoderLoading } = useGeocode();
const { requiredField } = useRequiredFieldRules();
const { regionForbidden, getRouteInfo } = useRouteInfo();
const { openCapacitorBrowser } = useCapacitorBrowser();
const {
  isHourly,
  isDelivery,
  isExperience,
  isCarRental,
  isRoute,
} = useTransferForm(transferType);

const { configs, showFormPrices } = storeToRefs(configsStore);
const {
  account,
  isFamilyUiType,
  isPartnerManager,
} = storeToRefs(accountStore);

const pax = computed(() => adults.value + childSeats.reduce((a, b) => a + transfer[b], 0));

const paramsToWatch = computed(() => [
  transferType.value,
  pax.value,
  transfer.from.point,
  transfer.to.point,
  transfer.trip_to.date,
  transfer.trip_to.date_end,
  transfer.trip_return.date,
  values.currency,
  values.distance_unit,
  locale.value,
].join());

const fieldsToSave = computed(() => ({
  adults,
  isRoundTrip,
  hourlyDifferentTo,
  passenger,
  transfer,
  transferType,
}));

const promoCodeRestricted = computed(() => routeInfo.value.promo_code_restricted);

const type = computed(() => {
  if (isHourly.value) {
    return 'hourly';
  }
  return isRoundTrip.value ? 'round_trip' : 'one_way';
});

const withToPoint = computed(() => !isCarRental.value
  && !isExperience.value
  && (!isHourly.value || hourlyDifferentTo.value));

const hourlyDuration = computed(() => {
  const { date, date_end } = transfer.trip_to;
  if (!(isHourly.value && date && date_end)) return null;

  return moment.duration(
    moment(date_end) - moment(date),
  ).asHours().toFixed(2);
});

const mustAcceptPartnership = computed(() => account.value
  && !account.value.partnership_agreement_accepted
  && account.value.partnership_agreement_accepted !== undefined);

const mustAcceptTerms = computed(() => !account.value || !account.value.terms_accepted || mustAcceptPartnership.value);

const hasPartnerPassenger = computed(() => isPartnerManager.value && !isFamilyUiType.value);

const pointDirection = computed(() => {
  if (isCarRental.value || isExperience.value) return null;
  if (!transfer.from.point) return 'from';
  if (withToPoint.value && !transfer.to.point) return 'to';
  return null;
});

const isFromAirport = computed(() => isPointWithType('from', 'airport'));
const isToAirport = computed(() => isPointWithType('to', 'airport'));

const showFromNameSignInput = computed(() => !isFamilyUiType.value || (isFromAirport.value || isPointWithType('from', 'train_station')));
const showToNameSignInput = computed(() => isRoundTrip.value
  && !showFromNameSignInput.value
  && (isToAirport.value || isPointWithType('to', 'train_station')));

function isPointWithType(point, pointType) {
  return transfer[point]?.types?.some((el) => el === pointType);
}

function sendAnalytics(event, extraParams) {
  const params = {
    account_id: account.value?.id.toString(),
    ...extraParams,
  };

  window.$logEvent?.firebase(event, params);
}

async function getRouteInfoForTransfer() {
  const { currency, distance_unit } = values;
  const params = {
    points: [transfer.from.point],
    countries: [transfer.from.country],
    with_prices: true,
    pax: pax.value,
    date_to: transfer.trip_to.date,
    date_return: transfer.trip_return.date,
    currency,
    distance_unit,
    delivery: isDelivery.value,
  };

  if (withToPoint.value) {
    params.points.push(transfer.to.point);
    params.countries.push(transfer.to.country);
  }
  if (isHourly.value) {
    params.duration = parseFloat(hourlyDuration.value) > 0 ? hourlyDuration.value : 1;
  }
  try {
    routeInfo.value = (await getRouteInfo(params)).data;
  } catch (err) {
    if (err.name !== 'CanceledError') {
      routeInfo.value = {
        distance_preferred: null,
        duration: null,
        hints_to_comments: [],
        min_date: null,
        prices: null,
        watertaxi: false,
      };
    }
  } finally {
    addressInputsRef.value?.validate();
  }
}

function setMapContainerHeight() {
  if (pointToChooseOnMap.value) {
    mapContainerRef.value.style.height = `${window.innerHeight - formRef.value.$el.offsetHeight + 16}px`;
    window.scrollTo(0, 0);
  }
}

function setDirection(key, value, chosenOnMap = false) {
  transfer[key] = {
    ...value,
    chosen_on_map: chosenOnMap,
  };

  if (!chosenOnMap && pointToChooseOnMap.value) {
    mapRef.value.setMapCenter(mapRef.value.pointToLatLng(value.point));
  }
}

let onMapDragEnd = async () => {
  const { center } = mapRef.value.map;
  const latLng = new google.maps.LatLng(center.lat(), center.lng());
  zoomMap.value = false;
  setDirection(pointToChooseOnMap.value, await geocode({ latLng }), true);
};

onMapDragEnd = debounce(onMapDragEnd, 500);

function pointToChooseOnMapAction(value) {
  if (!mapRef.value) {
    return;
  }
  google.maps.event.clearListeners(mapRef.value.map, 'center_changed');
  if (value) {
    const point = transfer[value]?.point;
    if (point) {
      mapRef.value.setMapCenter(mapRef.value.pointToLatLng(point), zoomMap.value);
    }
    zoomMap.value = true;
    mapRef.value.map.addListener('center_changed', onMapDragEnd);
    nextTick(setMapContainerHeight);
  } else {
    mapContainerRef.value.style.height = null;
  }
}

function iosNotch(active) {
  iosNotchEl?.classList.toggle('opacity-0', !active);
}

function backFromMap() {
  chooseOnMap(null);
}

function chooseOnMap(direction) {
  pointToChooseOnMap.value = direction;
  if (direction) {
    bus.emit('hide-footer');
    addressInputsRef.value.resetValidation();

    if (!window.history.state.chooseOnMap) {
      window.history.pushState({ chooseOnMap: true }, '');
    }
    window.addEventListener('popstate', backFromMap);
  } else {
    bus.emit('show-footer');

    if (window.history.state.chooseOnMap) {
      window.history.back();
    }
    window.removeEventListener('popstate', backFromMap);
  }
  document.body.classList.toggle('choosing-point-dialog', direction);
}

function replacePointFromQuery(direction, name, place_id, replaceTransfer) {
  if ((name || place_id) && replaceTransfer[direction]?.place_id !== place_id) {
    replaceTransfer[direction] = { name, place_id };
  }
}

function replaceRouteQuery() {
  router.replace({
    query: {
      ...route.query,
      transfer_type: undefined,
      promo_code: undefined,
      from_name: undefined,
      from_place_id: undefined,
      to_name: undefined,
      to_place_id: undefined,
      prefill_transfer_id: undefined,
      transfer: undefined,
      transport_type_ids: undefined,
    },
  });
}

function deletePastDate(value) {
  if (value) {
    const now = moment();
    const isInPast = (date) => date && now.isAfter(date);

    const { trip_return, trip_to } = value;

    if (isInPast(trip_return?.date)) {
      delete trip_return.date;
    }
    if (isInPast(trip_to?.date)) {
      delete trip_to.date;
    }
    if (isInPast(trip_to?.date_end)) {
      delete trip_to.date_end;
    }
  }
}

function prefillPax(target, source) {
  adults.value = +source.pax;
  adultsChangedManually.value = true;
  childSeats.forEach((el) => {
    target[el] = +source[el];
    adults.value -= +source[el];
  });
}

async function prefillTransferFromId() {
  if (!account.value) return;

  try {
    prefillTransferLoading.value = true;

    const tr = (await axios(`/api/transfers/${route.query.prefill_transfer_id}`, {
      skipErrorHandling: true,
    })).data.transfer;

    transferType.value = ['one_way', 'round_trip'].includes(tr.type) ? 'route' : tr.type;
    await nextTick();

    const getDate = (date) => date && moment.parseZone(date).format('YYYY-MM-DDTHH:mm:00');

    const localTransfer = {
      from: tr.from,
      to: tr.to || {},
      comment: tr.comment || '',
      name_sign: tr.name_sign || '',
      transport_type_ids: tr.transport_type_ids,
      trip_to: {
        date: getDate(tr.date_to_local),
        date_end: getDate(tr.date_end_local),
        flight_number: tr.flight_number,
      },
      trip_return: {
        date: getDate(tr.date_return_local),
        flight_number: tr.flight_number_return,
      },
    };

    deletePastDate(localTransfer);

    isRoundTrip.value = tr.type === 'round_trip';

    prefillPax(localTransfer, tr);

    Object.assign(transfer, localTransfer);
  } catch (err) {
    console.error(err);
  } finally {
    prefillTransferLoading.value = false;
  }
}

function prefillTransferFromLS() {
  const savedData = $q.localStorage.getItem('saved_transfer') || {};

  transferType.value = getTransferType(savedData.transferType, true);

  const data = savedData.transferType === transferType.value ? pick(savedData, fieldsToSaveList) : {};

  if (isRoute.value) {
    isRoundTrip.value = data.isRoundTrip;
  }
  if (data.adults) {
    adults.value = data.adults;
    adultsChangedManually.value = true;
  }
  deletePastDate(data.transfer);

  const localTransfer = data.transfer || {};
  localTransfer.from ||= {};
  localTransfer.to ||= {};

  const { query } = route;
  if (query.promo_code) {
    localTransfer.promo_code = query.promo_code;
  }
  if (query.transport_type_ids) {
    localTransfer.transport_type_ids = query.transport_type_ids;
  }
  ['from', 'to'].forEach((direction) => {
    replacePointFromQuery(
      direction,
      query[`${direction}_name`] || '',
      query[`${direction}_place_id`] || '',
      localTransfer,
    );
  });

  Object.assign(transfer, localTransfer);
}

async function prefillTransfer() {
  if (route.query.prefill_transfer_id) {
    await prefillTransferFromId();
  } else {
    prefillTransferFromLS();
  }

  hourlyDifferentTo.value = isHourly.value && !!transfer.to.name;

  replaceRouteQuery();

  watch(isDelivery, (value) => {
    if (value) {
      transfer.name_sign = '';
      transfer.trip_to.flight_number = '';
      transfer.trip_return = {
        date: '',
        flight_number: '',
      };
    } else {
      transfer.transport_type_ids = [];
    }

    transfer.comment = '';
    nextTick(() => commentInputRef.value?.resetValidation());
  });
}

async function fetchReasons() {
  reasons.value = (await axios('/api/transfer_reasons')).data.transfer_reasons;
}

function onInputClear(direction) {
  if (!pointToChooseOnMap.value) {
    sendAnalytics('passenger_clear_point', { direction });
  }
}

function onShowPromoCodeInput(value) {
  if (value) {
    sendTransferAnalytics('passenger_click_promocode');
  } else {
    transfer.promo_code = '';
    if (!isSubmitting.value) {
      isLoading.value = false;
    }
  }
}

async function initTransferRoute() {
  initGeocode();
  geocodeInitialized = true;

  if (['from', 'to'].includes(route.query.current_location)) {
    await addressInputsRef.value.currentLocation(route.query.current_location);
    if (pointDirection.value) {
      addressInputsRef.value.focus(pointDirection.value);
    }
  }

  const { from, to } = transfer;

  if ((from.name || from.place_id) && (!from.point || !from.types?.length)) {
    if (!from.place_id && configs.value.process_place_name) {
      await addressInputsRef.value.$refs.fromAutocompleteRef.processByName();
    } else {
      transfer.from = {
        ...from,
        ...await geocode(from),
      };
    }
  }
  if ((to.name || to.place_id) && (!to.point || !to.types?.length)) {
    transfer.to = {
      ...to,
      ...await geocode(to),
    };
  }
  showLoader.value = false;

  pointToChooseOnMapAction(pointToChooseOnMap.value);

  const { pointToChooseOnMap: pointToChooseOnMapQuery } = route.query;
  if (pointToChooseOnMapQuery) {
    if ($q.screen.lt.md) {
      await nextTick(() => {
        chooseOnMap(pointToChooseOnMapQuery);
      });
    } else {
      addressInputsRef.value.focus(pointToChooseOnMapQuery);
    }
  }
}

function sendTransferAnalytics(event) {
  sendAnalytics(event, {
    tab: transferType.value === 'route' ? 'transfer' : transferType.value,
  });
}

async function createTransfer() {
  isSubmitting.value = true;

  window.$logEvent?.firebase('passenger_transfer_full_form_click_enquire', {
    logged_in: !!account.value,
  });

  if (isWeb && (!account.value || account.value.require_contacts)) {
    showAuthModal();
    return;
  }
  isLoading.value = true;

  const data = { ...transfer };

  if (!showFormPrices.value) {
    data.transport_type_ids = [];
  }

  if (isDelivery.value) {
    data.transport_type_ids = ['parcel'];
    data.child_seats_booster = 0;
    data.child_seats_convertible = 0;
    data.child_seats_infant = 0;
  } else {
    data.pax = pax.value;
    data.partner_passenger = hasPartnerPassenger.value && passenger.value?.full_name
      ? passenger.value
      : undefined;
  }

  if (mustAcceptTerms.value) {
    data.passenger_account = {
      partnership_agreement_accepted: mustAcceptPartnership.value ? termsAccepted.value : undefined,
      terms_accepted: termsAccepted.value,
    };
  }

  try {
    const { transfer: createdTransfer } = (await axios.post('/api/transfers', {
      transfer: data,
    }, { timeout: 30_000 })).data;

    $q.localStorage.set('saved_transfer', {
      ...pick(fieldsToSave, fieldsToSaveList),
      transfer: pick(createdTransfer, [
        'from',
        'to',
        'airline_card',
        'airline_card_name',
        'airline_card_surname',
      ]),
    });

    if (route.query.suggestedFromTransfer) {
      const suggestedTransfers = $q.localStorage.getItem('suggestedTransfers') || [];
      $q.localStorage.set('suggestedTransfers', [
        ...suggestedTransfers,
        +route.query.suggestedFromTransfer,
        createdTransfer.id,
      ]);
    }

    if (props.redirectToCabinet) {
      window.location = `/${locale.value}/client/#/cabinet/transfers/${createdTransfer.id}?just_enquired=true`;
      return;
    }

    await accountStore.fetchAccount();

    if (props.redirectRouteName) {
      await router.push({
        name: props.redirectRouteName,
        params: {
          id: createdTransfer.id,
        },
        query: {
          just_enquired: true,
        },
      });
    }
  } catch (err) {
    console.error(err);
  } finally {
    isLoading.value = false;
    isSubmitting.value = false;
  }
}

async function fillPointOnClick({ latLng }, defaultDirection) {
  const direction = mapPinFor.value || pointDirection.value || defaultDirection;
  if (direction) {
    setDirection(direction, await geocode({ latLng }), true);
    if (defaultDirection) {
      chooseOnMap(direction);
    } else if (!mapPinFor.value && direction === 'from') {
      addressInputsRef.value.focus('to');
    }
  }
}

function onBottomIntersection({ isIntersecting }) {
  scrolledToBottom.value = isIntersecting;
}

function onScroll({ position }) {
  const iosNotchHeight = iosNotchEl?.offsetHeight || 0;
  iosNotch(position.top + iosNotchHeight >= mapContainerRef.value.offsetHeight);
}

function onTabVisible(tabName) {
  if (['car_rental', 'experience'].includes(tabName)) {
    sendAnalytics('passenger_saw_partners_tab', {
      partner: tabName === 'car_rental' ? 'getrentacar' : 'getexperience',
    });
  }
}

function onPartnerButtonClick() {
  sendAnalytics('passenger_click_partners_search', {
    partner: isCarRental.value ? 'getrentacar' : 'getexperience',
    type: transfer.from.place_id ? 'location' : 'no_location',
  });
}

function onValidationError(refLink) {
  refLink.$el.scrollIntoView({ block: 'center' });
  refLink.validateErrorHandler?.();
}

function showAuthModal(message, openedFrom = 'transfer_submit') {
  bus.emit('show-auth-modal', {
    openedFrom,
    message,
    onAuth() {
      if (isSubmitting.value) {
        formRef.value.submit();
      } else {
        promoCodeInputRef.value?.$refs.promoCodeRef?.checkPromoCode();
      }
    },
    onHide() {
      isSubmitting.value = false;
    },
  });
}

watch([showFromNameSignInput, showToNameSignInput], ([from, to]) => {
  if (!from && !to) {
    transfer.name_sign = '';
  }
});

watch(isFromAirport, (value) => {
  if (value) {
    sendTransferAnalytics('passenger_flight_shown');
  } else {
    transfer.trip_to.flight_number = '';
  }
});

watch(isToAirport, (value) => {
  if (value) {
    sendTransferAnalytics('passenger_flight_shown');
  } else {
    transfer.trip_return.flight_number = '';
  }
});

watch(transferType, () => {
  if (!withToPoint.value) {
    transfer.to = {};
  }

  isRoundTrip.value = false;
  transfer.trip_to.date_end = '';
  hourlyDifferentTo.value = false;

  if (!isClickPartnersTabSent && (isCarRental.value || isExperience.value)) {
    isClickPartnersTabSent = true;
    sendAnalytics('passenger_click_partners_tab', {
      partner: isCarRental.value ? 'getrentacar' : 'getexperience',
    });
  }

  window.$logEvent?.firebase(transferType.value, { event_category: 'tab' });
});

watch(isRoundTrip, (value) => {
  if (value) {
    sendTransferAnalytics('passenger_add_return');
  } else {
    transfer.trip_return = {
      date: '',
      flight_number: '',
    };
  }
});

watch(fieldsToSave, () => {
  $q.localStorage.set('saved_transfer', pick(fieldsToSave.value, fieldsToSaveList));
}, { deep: true });

watch(paramsToWatch, getRouteInfoForTransfer);

watch(pointToChooseOnMap, (value) => pointToChooseOnMapAction(value));

watch(promoCodeRestricted, (value) => {
  if (value) {
    transfer.promo_code = '';
  }
});

watch(() => transfer.to, () => {
  if (pointToChooseOnMap.value) {
    pointToChooseOnMapAction('to');
  }
});

watch(() => transfer.from, () => {
  if (pointToChooseOnMap.value) {
    pointToChooseOnMapAction('from');
  }
});

watch(() => transfer.transport_type_ids, (value) => {
  if (!adultsChangedManually.value && value.length) {
    adults.value = Math.max(...value.map((el) => adultSeats[el] || 1));
  }
});

watch(mapPinFor, (value) => {
  if (value) {
    sendTransferAnalytics(`passenger_open_${value}`);
  }
});

watch(route, async ({ query }) => {
  const keys = [
    'transfer_type',
    'promo_code',
    'from_name',
    'from_place_id',
    'to_name',
    'to_place_id',
    'prefill_transfer_id',
  ];
  if (!keys.some((key) => query[key])) return;

  if (query.prefill_transfer_id) {
    await prefillTransferFromId();
  }

  if (query.transfer_type) {
    transferType.value = getTransferType(query.transfer_type, true);
    await nextTick();
  }

  if (query.promo_code) {
    transfer.promo_code = query.promo_code;
  }

  await Promise.all(['from', 'to'].map(async (direction) => {
    const name = query[`${direction}_name`] || '';
    const place_id = query[`${direction}_place_id`] || '';

    if (!name && !place_id) return;

    if (geocodeInitialized) {
      transfer[direction] = await geocode({ name, place_id });
    } else {
      replacePointFromQuery(direction, name, place_id, transfer);
    }
  }));

  hourlyDifferentTo.value = isHourly.value && !!transfer.to.name;

  replaceRouteQuery();
}, { flush: 'post' });

if (isCapacitor) {
  prefillTransfer();
}

onBeforeUnmount(() => {
  document.body.classList.remove('choosing-point-dialog');
  iosNotch(true);
  window.removeEventListener('popstate', backFromMap);
  window.removeEventListener('resize', setMapContainerHeight);
});

onMounted(() => {
  iosNotchEl = document.querySelector('.ios-notch');
  iosNotch(false);
  if (isWeb) {
    prefillTransfer();
  }

  if (account.value) {
    fetchReasons();
  }

  const btnGetOffers = floatingBtnGetOffersRef.value?.$el;
  if (btnGetOffers) {
    btnGetOffers.style.width = `${btnGetOffers.getBoundingClientRect().width}px`;
  }

  window.addEventListener('resize', setMapContainerHeight);
});
</script>

<style lang="scss">
.toggle-margin .q-toggle__inner {
  margin-left: -8px;
}

.choosing-point-dialog {
  overflow: hidden;
  padding: 0 !important;
  margin: 0 !important;

  .q-header,
  .q-footer,
  .sub {
    display: none;
  }
}

.page-container {
  .form-container {
    max-width: 100vw;
    border-radius: 16px 16px 0 0;
    z-index: 10;
    transition: margin-top .3s ease;

    @at-root .choosing-point-dialog & {
      width: 100%;
      box-shadow: 0 -3px 4px -3px #000;
      margin-top: -16px;

      > div {
        padding: 0 16px;
      }

      .inputs {
        border: none;
        padding-left: 0;
        padding-right: 0;
      }
    }

    @media screen and (max-width: $breakpoint-sm-max) {
      box-shadow: 0 -3px 4px -3px #000;
      margin-top: -16px;

      > div {
        padding: 0 16px;
      }
    }
  }

  %height {
    height: 450px;
    max-height: calc(100vh - 400px + env(safe-area-inset-top, 0px));
    min-height: 300px; // fix zoom at fitBounds()
  }

  .map-container {
    @extend %height;

    position: relative;
    width: 100vw;
    margin: 0;
    z-index: 0;

    .pin-on-map {
      top: calc(50% - 19px);
      text-shadow: 0 -1px 5px #000;
    }

    @at-root .choosing-point-dialog & {
      max-height: unset !important;
    }

    @at-root .capacitor:not(.choosing-point-dialog) & .google-map {
      @extend %height;

      position: fixed;
      width: 100%;
    }
  }
}
</style>

<style lang="scss" scoped>
h1 {
  font-size: 32px;

  @media (min-width: $breakpoint-lg-min) {
    font-size: 42px;
  }
}

.map {
  margin-top: calc(constant(safe-area-inset-top) * -1);
  margin-top: calc(env(safe-area-inset-top) * -1);
}

.map :deep(.map-distance-block) {
  @media screen and (max-width: $breakpoint-sm-max) {
    margin-bottom: 16px;
  }
}

.terms-field {
  :deep(.q-field__control) {
    a {
      color: black;
      text-decoration: underline;
    }

    .q-field__prepend {
      padding: 0;
    }
  }

  &.q-field--error :deep(.q-field__control) {
    &::before {
      border-bottom: 2px solid $negative;
    }

    .q-checkbox__bg {
      border-color: $negative;
    }
  }
}

.secondary {
  margin-left: 16px !important;
}

.observer {
  height: 1px;
  margin-top: -12px;
}

.sticky-btn {
  position: sticky;
  bottom: 30px;
  z-index: 100;

  .q-btn {
    transition: width .3s ease;

    &::before {
      box-shadow: 0 7px 8px -4px rgb(#000, .20),
        0 12px 17px 2px rgb(#000, .14),
        0 5px 22px 4px rgb(#000, .12);
      transition: box-shadow .3s ease;
    }

    &.full-width::before {
      box-shadow: none;
    }
  }

  @media screen and (max-width: $breakpoint-xs-max) {
    bottom: 80px;
  }
}

.transfer-type-toggle {
  padding-left: 0;
  padding-right: 0;
}

.q-inner-loading {
  z-index: 10;

  @media screen and (max-width: $breakpoint-sm-max) {
    position: fixed;
  }
}
</style>
